/**
 * PluginRenderSection — 单个 root definition 的完整渲染（含 Renderer + 工具栏 + 状态 UI）。
 *
 * 从原 PluginBlock.tsx 抽离（P3 #5），让 PluginBlock 只承担 scope-chain + 三路分派职责。
 * 行为保持不变；hooks 顺序与原文件一致。
 */

import type {
    PluginDefinition,
    RenderMode,
    UseBinding,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { ContinuationProvider } from "@magic-md/plugin-sdk";
import type {
    RendererConfigChangeOptions,
    RendererLayout,
    RendererProps,
} from "@magic-md/plugin-sdk";
import { AlertTriangle, Loader2, Maximize2, Minimize2, Play, RefreshCw, StretchHorizontal } from "lucide-react";
import { createElement, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ComponentType, CSSProperties, MouseEvent, RefObject } from "react";
import { useJustFinishedFlash } from "../../../hooks/useJustFinishedFlash";
import {
    PREVIEW_HORIZONTAL_PADDING,
    PREVIEW_WIDTH_PRESETS,
    type PreviewWidthControl,
    clampPreviewViewportWidth,
    resolvePreviewViewportWidth,
} from "../../../lib/preview-width";
import { cn } from "../../../lib/utils";
import { resolveImportedSymbolNamespace } from "../../../preview-logic";
import {
    getMissingRequiredTemplateInputsForUse,
    isUseBindingBlockedAsTemplate,
} from "../../../lib/template-unit-state";
import {
    getThemeAccentStyle,
    getThemeAccentValue,
} from "../../../theme/runtime/theme-accents";
import { buildAnnotationOutputHash } from "../../annotations/hash";
import { AnnotationBlockFooterControl } from "../../annotations/AnnotationBlockTarget";
import { AnnotationRenderedTextScope } from "../../annotations/rendered-text";
import { useDocRendererContinuation } from "../../continuation/ContinuationResolver";
import { PluginErrorBoundary } from "../../PluginErrorBoundary";
import { useDocContext, useDocOutput } from "../contexts";
import {
    useDefinitionExecutionState,
    useImportExecution,
} from "../contexts/ExecutionContext";
import {
    CollapsedUnitRenderHeader,
    getPluginDisplayName,
    getPluginTechnicalLabel,
    type CollapsedUnitRenderStatus,
} from "./CollapsedUnitRenderHeader";
import { getRendererAttrs, readRenderExpanded } from "./render-attrs";
import { RenderUnitBadge, type RenderUnitBadgeProps } from "./RenderUnitBadge";
import { RenderUnitFooterDisclosure } from "./RenderUnitFooterDisclosure";
import { TemplateRenderPlaceholder } from "./TemplateRenderPlaceholder";
import { TemplateUsageWarning } from "./TemplateUsageWarning";
import {
    type UnitFooterCopyAction,
    stringifyUnitFooterJson,
    UnitFooterCopyButton,
} from "./UnitFooterCopyButton";
import { UnitDependencyFooterButton } from "./UnitDependencyFooterButton";

const PLUGIN_RENDER_PRELOAD_MARGIN = "900px 0px";
const PLUGIN_RENDER_PRELOAD_MARGIN_PX = 900;
const useIsomorphicLayoutEffect =
    typeof window === "undefined" ? useEffect : useLayoutEffect;

const WORKSPACE_FULLSCREEN_SELECTOR = "[data-magic-editor-preview-workspace='true']";
const INPUTS_CONFIRM_BLUR_SELECTOR = "[data-magic-md-input-confirm='true']";
const INPUTS_COMMIT_PROMISE_PROPERTY = "__magicMdInputCommitPromise";

type InputConfirmElement = Element & {
    [INPUTS_COMMIT_PROMISE_PROPERTY]?: Promise<unknown>;
};

type WorkspaceFullscreenRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

function isExternalStubOutput(value: unknown): boolean {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const keys = Object.keys(value);
    return keys.length === 1 && keys[0] === "__external";
}

function findWorkspaceFullscreenHost(element: HTMLElement | null): HTMLElement | null {
    return element?.closest<HTMLElement>(WORKSPACE_FULLSCREEN_SELECTOR) ?? null;
}

function readWorkspaceFullscreenRect(host: HTMLElement | null): WorkspaceFullscreenRect | null {
    if (typeof window === "undefined") return null;

    if (host) {
        const rect = host.getBoundingClientRect();
        return {
            top: Math.round(rect.top),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    }

    const visualViewport = window.visualViewport;
    return {
        top: Math.round(visualViewport?.offsetTop ?? 0),
        left: Math.round(visualViewport?.offsetLeft ?? 0),
        width: Math.round(visualViewport?.width ?? window.innerWidth),
        height: Math.round(visualViewport?.height ?? window.innerHeight),
    };
}

function readFullscreenAvailableWidth(rect: WorkspaceFullscreenRect | null): number | null {
    if (rect) {
        return Math.max(1, rect.width - PREVIEW_HORIZONTAL_PADDING);
    }
    if (typeof window === "undefined") return null;
    return Math.max(1, window.innerWidth - PREVIEW_HORIZONTAL_PADDING);
}

async function waitForPendingInputCommit(target: EventTarget | null | undefined) {
    if (!(target instanceof Element)) return;
    const confirmTarget = target.closest(
        INPUTS_CONFIRM_BLUR_SELECTOR,
    ) as InputConfirmElement | null;
    const promise = confirmTarget?.[INPUTS_COMMIT_PROMISE_PROPERTY];
    if (promise && typeof promise.then === "function") {
        await promise;
    }
}

interface TemplatePlaceholderPresentation {
    kindLabel: string;
    statusLabel?: string;
    message?: string;
    paramLabel?: string | null;
    configureLabel?: string;
    badgeMain: string;
    badgeSub: string;
}

function getTemplatePlaceholderPresentation(
    t: ReturnType<typeof useT>,
    definitionId: string,
    definition: PluginDefinition,
    useBinding: UseBinding | undefined,
    missingRequired: readonly string[],
): TemplatePlaceholderPresentation {
    if (!useBinding) {
        return {
            kindLabel: t("render.kind.definition"),
            badgeMain: "D",
            badgeSub: "T",
        };
    }

    if (useBinding.template) {
        return {
            kindLabel: "Use",
            statusLabel: t("render.useTemplate.status"),
            message: t("render.useTemplate.message"),
            paramLabel: definition.params?.length
                ? t("render.params", { params: definition.params.join(", ") })
                : null,
            configureLabel: t("render.configureUse"),
            badgeMain: "U",
            badgeSub: "D",
        };
    }

    const missing = [...missingRequired];
    const missingText = missing.join(", ");

    return {
        kindLabel: "Use",
        statusLabel: t("render.useIncomplete.status"),
        message: t("render.useIncomplete.message", {
            missing: missingText,
            id: definitionId,
        }),
        paramLabel: missing.length > 0
            ? t("render.missingParams", { params: missingText })
            : null,
        configureLabel: t("render.configureUse"),
        badgeMain: "U",
        badgeSub: "D",
    };
}

function formatPreviewWidthLabel(
    t: ReturnType<typeof useT>,
    control: PreviewWidthControl,
    customWidth: number | null,
): string {
    if (control === "custom") {
        return customWidth
            ? t("previewWidth.customWithWidth", {
                width: Math.round(customWidth),
            })
            : t("previewWidth.custom");
    }
    return t(`previewWidth.${control}`);
}

function isElementNearViewport(target: HTMLElement): boolean {
    if (typeof window === "undefined") return true;
    const rect = target.getBoundingClientRect();
    const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
    return (
        rect.bottom >= -PLUGIN_RENDER_PRELOAD_MARGIN_PX &&
        rect.top <= viewportHeight + PLUGIN_RENDER_PRELOAD_MARGIN_PX
    );
}

function useLazyPluginMount(
    enabled: boolean,
    targetRef: RefObject<HTMLElement | null>,
): boolean {
    const [shouldMount, setShouldMount] = useState(() =>
        !enabled ||
        typeof window === "undefined" ||
        typeof IntersectionObserver === "undefined",
    );

    useIsomorphicLayoutEffect(() => {
        if (!enabled) {
            setShouldMount(true);
            return;
        }

        if (shouldMount) return;

        if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
            setShouldMount(true);
            return;
        }

        const target = targetRef.current;
        if (!target) {
            setShouldMount(true);
            return;
        }
        if (isElementNearViewport(target)) {
            setShouldMount(true);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry?.isIntersecting && entry?.intersectionRatio === 0) return;
            setShouldMount(true);
            observer.disconnect();
        }, {
            root: null,
            rootMargin: PLUGIN_RENDER_PRELOAD_MARGIN,
            threshold: 0,
        });

        observer.observe(target);
        return () => observer.disconnect();
    }, [enabled, shouldMount, targetRef]);

    return shouldMount;
}

function useStableEventCallback<T extends (...args: never[]) => unknown>(callback: T): T {
    const callbackRef = useRef(callback);
    useIsomorphicLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    return useCallback(((...args: Parameters<T>) => callbackRef.current(...args)) as T, []);
}

function useStableOptionalEventCallback<T extends (...args: never[]) => unknown>(
    callback: T | undefined,
): T | undefined {
    const callbackRef = useRef(callback);
    useIsomorphicLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    const hasCallback = !!callback;
    return useMemo(() => {
        if (!hasCallback) return undefined;
        return ((...args: Parameters<T>) => callbackRef.current?.(...args)) as T;
    }, [hasCallback]);
}

type PluginRendererHostProps = RendererProps & {
    Renderer: ComponentType<RendererProps>;
};

const MemoizedPluginRendererHost = memo(function MemoizedPluginRendererHost({
    Renderer,
    ...props
}: PluginRendererHostProps) {
    return <Renderer {...props} />;
});

function DeferredPluginRendererPlaceholder({
    definitionId,
    pluginName,
}: {
    definitionId: string;
    pluginName: string;
}) {
    return (
        <div
            className="flex min-h-24 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-3 py-4 text-xs text-muted-foreground"
            data-plugin-render-deferred="true"
        >
            <span className="font-mono">{definitionId}</span>
            <span className="mx-1.5 text-muted-foreground/40">·</span>
            <span>{pluginName}</span>
        </div>
    );
}

export function PluginRenderEmptyState({
    definitionId,
    pluginDisplayName,
    pluginTechnicalLabel,
    unitBadge = { main: "D" },
    statusLabel,
    message,
    canRun,
    isRunning,
    isLoading,
    hasError,
    errorMessage,
    onRun,
}: {
    definitionId: string;
    pluginDisplayName: string;
    pluginTechnicalLabel: string;
    unitBadge?: Pick<RenderUnitBadgeProps, "main" | "sub" | "sup" | "template">;
    statusLabel: string;
    message: string;
    canRun: boolean;
    isRunning: boolean;
    isLoading: boolean;
    hasError: boolean;
    errorMessage: string | null;
    onRun: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
    const t = useT("editor");
    const accentStyle = isRunning || isLoading
        ? getThemeAccentStyle("running")
        : hasError
            ? getThemeAccentStyle("error")
            : undefined;

    return (
        <div
            className={cn(
                "my-1.5 rounded-lg border border-dashed bg-background px-3 py-2.5",
                "transition-colors",
                isRunning || isLoading || hasError
                    ? "theme-accent-banner"
                    : "border-border",
            )}
            style={accentStyle}
            data-plugin-render-empty="true"
        >
            <div className="flex min-w-0 items-center gap-3">
                <RenderUnitBadge {...unitBadge} />

                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-mono text-[13px] font-medium text-foreground">
                            {definitionId}
                        </span>
                        <span
                            className={cn(
                                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                                hasError
                                    ? "theme-accent-chip"
                                    : isRunning || isLoading
                                        ? "theme-accent-chip"
                                        : "bg-muted text-muted-foreground",
                            )}
                            style={accentStyle}
                        >
                            {statusLabel}
                        </span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="shrink-0">{t("render.component")}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="truncate">{pluginDisplayName}</span>
                        <span
                            className="hidden min-w-0 truncate font-mono text-muted-foreground/50 sm:inline"
                            title={pluginTechnicalLabel}
                        >
                            {pluginTechnicalLabel}
                        </span>
                    </div>
                    <div
                        className={cn(
                            "mt-1 truncate text-[11px]",
                            hasError
                                ? "theme-accent-text"
                                : "text-muted-foreground/70",
                        )}
                        style={hasError ? getThemeAccentStyle("error") : undefined}
                        title={errorMessage ?? message}
                    >
                        {hasError ? errorMessage : message}
                    </div>
                </div>

                {hasError && (
                    <AlertTriangle
                        size={15}
                        className="shrink-0 theme-accent-text"
                        style={getThemeAccentStyle("error")}
                        aria-label={t("render.status.failed")}
                    />
                )}
                {!hasError && isLoading && (
                    <Loader2
                        size={15}
                        className="shrink-0 animate-spin theme-accent-text"
                        style={getThemeAccentStyle("running")}
                        aria-label={t("status.loading")}
                    />
                )}

                {canRun && (
                    <button
                        type="button"
                        data-magic-md-input-confirm="true"
                        className={cn(
                            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-[11px] font-medium",
                            "transition-colors",
                            isRunning || isLoading
                                ? "cursor-wait bg-muted text-muted-foreground"
                                : "bg-primary text-primary-foreground hover:bg-primary/90",
                        )}
                        onClick={onRun}
                        disabled={isRunning || isLoading}
                        aria-label={t("render.runUnit", { id: definitionId })}
                    >
                        {isRunning || isLoading ? (
                            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <Play size={13} aria-hidden="true" />
                        )}
                        <span>{t("render.action.run")}</span>
                    </button>
                )}
            </div>
        </div>
    );
}

export const PluginRenderSection = memo(function PluginRenderSection({
    definitionId,
    attrs,
    mode,
    defaultExpanded = true,
}: {
    definitionId: string;
    attrs: Record<string, string>;
    mode: RenderMode;
    defaultExpanded?: boolean;
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const docOutput = useDocOutput(definitionId);
    const unitExecution = useDefinitionExecutionState(definitionId);
    const continuationCtx = useDocRendererContinuation(mode);

    const isInline = mode === "inline";
    const Wrapper = isInline ? "span" : "div";

    const definition = doc.definitions.get(definitionId);
    const plugin = definition ? doc.registry.get(definition.pluginName) : undefined;
    const isTemplateDefinition = !!definition?.template;
    const rawOutput = (definition
        ? docOutput ?? definition.output
        : undefined) as Record<string, unknown> | undefined;
    const output = isExternalStubOutput(rawOutput) ? undefined : rawOutput;
    const useBinding = useMemo(
        () => doc.useBindings?.find((binding) => binding.id === definitionId),
        [definitionId, doc.useBindings],
    );
    const missingRequiredTemplateInputs = useMemo(
        () =>
            useBinding
                ? getMissingRequiredTemplateInputsForUse(useBinding, {
                    definitions: doc.definitions,
                    contentDefinitions: doc.contentDefinitions,
                    uses: doc.useBindings,
                })
                : [],
        [doc.contentDefinitions, doc.definitions, doc.useBindings, useBinding],
    );
    const blocksAsTemplate = useBinding
        ? isUseBindingBlockedAsTemplate(useBinding, missingRequiredTemplateInputs)
        : isTemplateDefinition;
    const useExternalRef = useBinding?.externalRef;
    const deferredExternalRef = definition?.externalRef ?? useExternalRef;
    const deferredOutputLocator = deferredExternalRef?.locator;
    const importNamespace = useMemo(
        () =>
            resolveImportedSymbolNamespace(
                definitionId,
                doc.importBindings,
                doc.localExecutionUnitIds,
        ),
        [definitionId, doc.importBindings, doc.localExecutionUnitIds],
    );
    const importExecution = useImportExecution(importNamespace);
    const isImportRunning = importExecution?.status === "running";
    const canRunImport = !!importNamespace && !!doc.onRunImport;
    const canRunDefinition = (useBinding ? !!(doc.onRunUse || doc.onRun) : !!doc.onRun) && !blocksAsTemplate;

    // Execution state from ExecutionContext
    const isRunning = unitExecution.isRunning;
    const execStatus = unitExecution.status;
    const isPinned = unitExecution.isPinned;
    const execDuration = unitExecution.duration;
    const execComputedAt = unitExecution.computedAt;
    const execProgress = unitExecution.progress;

    // Error from output payload or execution result
    const outputError = output?.__error;
    const outputErrorMsg = typeof outputError === "object" && outputError !== null
        ? (outputError as { message?: string }).message
        : null;
    const execErrorMsg = unitExecution.errorMessage ?? null;
    const errorMessage = outputErrorMsg || execErrorMsg || null;
    const hasError = !!errorMessage;

    const handleAction = useCallback(
        async (actionName: string, params?: Record<string, unknown>) => {
            if (doc.executable && doc.onAction) {
                return doc.onAction(definitionId, actionName, params);
            }
            return undefined;
        },
        [definitionId, doc.executable, doc.onAction],
    );

    const handleApi = useCallback(
        async <TData = unknown,>(apiName: string, params?: Record<string, unknown>) => {
            if (doc.executable && doc.onApi) {
                return doc.onApi<TData>(definitionId, apiName, params);
            }
            return {
                success: false,
                message: "Plugin runtime API is not available",
            };
        },
        [definitionId, doc.executable, doc.onApi],
    );

    const handleConfigChange = useCallback(
        async (nextConfig: Record<string, unknown>, options?: RendererConfigChangeOptions) => {
            if (!doc.onDefinitionConfigChange) return undefined;
            if (!options) return doc.onDefinitionConfigChange(definitionId, nextConfig);
            return doc.onDefinitionConfigChange(definitionId, nextConfig, options);
        },
        [definitionId, doc.onDefinitionConfigChange],
    );

    const handleRun = useCallback((event?: MouseEvent<HTMLElement>) => {
        if (canRunDefinition && !isRunning) {
            const target = event?.currentTarget;
            void (async () => {
                await waitForPendingInputCommit(target);
                if (useBinding && doc.onRunUse) {
                    doc.onRunUse(definitionId);
                    return;
                }
                doc.onRun?.(definitionId);
            })();
        }
    }, [canRunDefinition, definitionId, doc, isRunning, useBinding]);

    const handleRunImport = useCallback((event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        if (!canRunImport || !importNamespace || isImportRunning) return;
        doc.onRunImport?.(importNamespace);
    }, [canRunImport, doc.onRunImport, importNamespace, isImportRunning]);
    const canRunPrimary = canRunDefinition || canRunImport;
    const isPrimaryRunning = isRunning || isImportRunning;
    const handlePrimaryRun = canRunDefinition ? handleRun : handleRunImport;

    // Track "just finished" flash state — centralized in useJustFinishedFlash
    const justFinished = useJustFinishedFlash(execStatus, { suppress: hasError });
    const initialExpanded = readRenderExpanded(attrs, defaultExpanded);
    const [expanded, setExpanded] = useState(initialExpanded);
    const rendererAttrs = useMemo(() => getRendererAttrs(attrs), [attrs]);
    const stableRendererAction = useStableEventCallback(handleAction);
    const stableRendererApi = useStableEventCallback(handleApi);
    const stableRendererConfigChange = useStableOptionalEventCallback(
        doc.onDefinitionConfigChange ? handleConfigChange : undefined,
    );
    const renderContainerRef = useRef<HTMLDivElement | null>(null);
    const renderBodyRef = useRef<HTMLDivElement | null>(null);
    const fullscreenResizeRef = useRef<{
        side: "left" | "right";
        startX: number;
        startWidth: number;
    } | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [workspaceFullscreenRect, setWorkspaceFullscreenRect] =
        useState<WorkspaceFullscreenRect | null>(null);
    const [renderViewport, setRenderViewport] = useState<{ width: number; height: number } | null>(null);
    const [fullscreenWidthPreset, setFullscreenWidthPreset] =
        useState<PreviewWidthControl>("fill");
    const [customFullscreenWidth, setCustomFullscreenWidth] = useState<number | null>(null);
    const [isFullscreenWidthMenuOpen, setIsFullscreenWidthMenuOpen] = useState(false);
    const [isFullscreenWidthDragging, setIsFullscreenWidthDragging] = useState(false);
    const deferRendererMount = !isInline && !isFullscreen && !blocksAsTemplate;
    const shouldMountRenderer = useLazyPluginMount(deferRendererMount, renderContainerRef);
    const [isLoadingDeferredOutput, setIsLoadingDeferredOutput] = useState(false);
    const [deferredOutputError, setDeferredOutputError] = useState<string | null>(null);
    const deferredOutputRequestKeyRef = useRef<string | null>(null);

    useEffect(() => {
        setExpanded(initialExpanded);
    }, [definitionId, initialExpanded]);

    useEffect(() => {
        if (!isFullscreen || typeof window === "undefined") {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (isFullscreenWidthMenuOpen) {
                setIsFullscreenWidthMenuOpen(false);
                return;
            }
            setIsFullscreen(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isFullscreen, isFullscreenWidthMenuOpen]);

    useEffect(() => {
        if (!isFullscreen) {
            setWorkspaceFullscreenRect(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(false);
            return;
        }

        const container = renderContainerRef.current;
        const workspaceHost = findWorkspaceFullscreenHost(container);

        const updateRect = () => {
            const next = readWorkspaceFullscreenRect(workspaceHost);
            if (!next) return;
            setWorkspaceFullscreenRect((current) =>
                current?.top === next.top &&
                    current?.left === next.left &&
                    current?.width === next.width &&
                    current?.height === next.height
                    ? current
                    : next,
            );
        };

        updateRect();
        window.addEventListener("resize", updateRect);
        window.addEventListener("scroll", updateRect, true);

        if (typeof ResizeObserver === "undefined" || !workspaceHost) {
            return () => {
                window.removeEventListener("resize", updateRect);
                window.removeEventListener("scroll", updateRect, true);
            };
        }

        const observer = new ResizeObserver(updateRect);
        observer.observe(workspaceHost);
        return () => {
            window.removeEventListener("resize", updateRect);
            window.removeEventListener("scroll", updateRect, true);
            observer.disconnect();
        };
    }, [isFullscreen]);

    useEffect(() => {
        if (!isFullscreen) {
            setRenderViewport(null);
            return;
        }

        const element = renderBodyRef.current;
        if (!element || typeof window === "undefined") return;

        const updateViewport = () => {
            const rect = element.getBoundingClientRect();
            const next = {
                width: Math.round(rect.width),
                height: Math.round(rect.height),
            };
            setRenderViewport((current) =>
                current?.width === next.width && current?.height === next.height
                    ? current
                    : next,
            );
        };

        updateViewport();
        window.addEventListener("resize", updateViewport);

        if (typeof ResizeObserver === "undefined") {
            return () => window.removeEventListener("resize", updateViewport);
        }

        const observer = new ResizeObserver(updateViewport);
        observer.observe(element);
        return () => {
            window.removeEventListener("resize", updateViewport);
            observer.disconnect();
        };
    }, [isFullscreen]);

    const fullscreenAvailableWidth = isFullscreen
        ? readFullscreenAvailableWidth(workspaceFullscreenRect)
        : null;
    const resolvedFullscreenViewportWidth = isFullscreen
        ? resolvePreviewViewportWidth(
            fullscreenWidthPreset,
            customFullscreenWidth,
            fullscreenAvailableWidth,
        )
        : "fill";
    const currentFullscreenWidthLabel = formatPreviewWidthLabel(
        t,
        fullscreenWidthPreset,
        customFullscreenWidth,
    );

    const handleToggleFullscreen = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (isFullscreen) {
                setIsFullscreen(false);
                return;
            }

            const workspaceHost = findWorkspaceFullscreenHost(renderContainerRef.current);
            setWorkspaceFullscreenRect(readWorkspaceFullscreenRect(workspaceHost));
            setFullscreenWidthPreset("fill");
            setCustomFullscreenWidth(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreen(true);
        },
        [isFullscreen],
    );

    const handleFullscreenWidthPresetSelect = useCallback(
        (preset: Exclude<PreviewWidthControl, "custom">) => {
            setFullscreenWidthPreset(preset);
            if (preset === "fill") {
                setCustomFullscreenWidth(null);
            }
            setIsFullscreenWidthMenuOpen(false);
        },
        [],
    );

    const startFullscreenWidthResize = useCallback(
        (side: "left" | "right", event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = renderBodyRef.current?.getBoundingClientRect();
            const currentWidth =
                rect?.width ??
                (resolvedFullscreenViewportWidth === "fill"
                    ? fullscreenAvailableWidth
                    : resolvedFullscreenViewportWidth);
            if (!currentWidth) return;
            fullscreenResizeRef.current = {
                side,
                startX: event.clientX,
                startWidth: currentWidth,
            };
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(true);
        },
        [fullscreenAvailableWidth, resolvedFullscreenViewportWidth],
    );

    const resetFullscreenWidth = useCallback(() => {
        setFullscreenWidthPreset("fill");
        setCustomFullscreenWidth(null);
        setIsFullscreenWidthMenuOpen(false);
    }, []);

    useEffect(() => {
        if (!isFullscreenWidthDragging || typeof window === "undefined") return;

        const onMove = (event: globalThis.MouseEvent) => {
            const state = fullscreenResizeRef.current;
            const availableWidth = readFullscreenAvailableWidth(workspaceFullscreenRect);
            if (!state || !availableWidth) return;
            const delta = event.clientX - state.startX;
            const nextWidth =
                state.side === "right"
                    ? state.startWidth + delta
                    : state.startWidth - delta;
            setFullscreenWidthPreset("custom");
            setCustomFullscreenWidth(clampPreviewViewportWidth(nextWidth, availableWidth));
        };

        const onUp = () => {
            fullscreenResizeRef.current = null;
            setIsFullscreenWidthDragging(false);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isFullscreenWidthDragging, workspaceFullscreenRect]);

    const rendererLayout = useMemo<RendererLayout>(
        () => ({
            presentation: isFullscreen ? "fullscreen" : "normal",
            fillHeight: isFullscreen,
            viewport: isFullscreen ? renderViewport : null,
        }),
        [isFullscreen, renderViewport],
    );

    const renderBodyStyle =
        isFullscreen
            ? ({
                ...(renderViewport
                    ? {
                        "--magic-render-viewport-width": `${renderViewport.width}px`,
                        "--magic-render-viewport-height": `${renderViewport.height}px`,
                    }
                    : {}),
                width: resolvedFullscreenViewportWidth === "fill"
                    ? "100%"
                    : `${resolvedFullscreenViewportWidth}px`,
                maxWidth: "100%",
            } as CSSProperties)
            : undefined;

    const fullscreenContainerStyle =
        isFullscreen && workspaceFullscreenRect
            ? ({
                top: workspaceFullscreenRect.top,
                left: workspaceFullscreenRect.left,
                width: workspaceFullscreenRect.width,
                height: workspaceFullscreenRect.height,
            } as CSSProperties)
            : undefined;

    const copyReferencePath = `${definitionId}.output`;
    const copyReferenceText = `$ref{${copyReferencePath}}`;
    const copyActions = useMemo<UnitFooterCopyAction[]>(() => {
        if (!definition) return [];

        const defineSnippet = [
            `::define[${definitionId}]{plugin="${definition.pluginName}"}`,
            "```config",
            stringifyUnitFooterJson(definition.config),
            "```",
            "::",
        ].join("\n");

        return [
            {
                key: "copy-reference",
                label: t("copy.reference"),
                text: copyReferenceText,
                icon: "reference",
            },
            {
                key: "copy-path",
                label: t("copy.path"),
                text: copyReferencePath,
                icon: "path",
            },
            {
                key: "copy-id",
                label: t("copy.id"),
                text: definitionId,
                icon: "id",
            },
            {
                key: "copy-config",
                label: t("copy.config"),
                text: stringifyUnitFooterJson(definition.config),
                icon: "config",
                separator: true,
            },
            ...(output
                ? [{
                    key: "copy-output",
                    label: t("copy.output"),
                    getText: () => stringifyUnitFooterJson(output),
                    icon: "output" as const,
                }]
                : []),
            {
                key: "copy-render",
                label: t("copy.renderStatement"),
                text: `::render[${definitionId}]::`,
                icon: "render",
                separator: true,
            },
            {
                key: "copy-definition-snippet",
                label: t("copy.definitionSnippet"),
                text: defineSnippet,
                icon: "snippet",
            },
            ...(doc.documentPath
                ? [{
                    key: "copy-document-path",
                    label: t("copy.documentPath"),
                    text: doc.documentPath,
                    icon: "document" as const,
                    separator: true,
                }]
                : []),
        ];
    }, [
        copyReferencePath,
        copyReferenceText,
        definition,
        definitionId,
        doc.documentPath,
        output,
        t,
    ]);
    const shouldPrepareRenderedOutput =
        !blocksAsTemplate && (isInline || isFullscreen || shouldMountRenderer);
    const deferredOutputRequestTarget = deferredExternalRef || importNamespace
        ? definitionId
        : null;
    const canLoadDeferredOutput = Boolean(
        !output &&
        deferredOutputRequestTarget &&
        doc.onLoadOutput &&
        shouldPrepareRenderedOutput,
    );

    useEffect(() => {
        if (!output) return;
        setIsLoadingDeferredOutput(false);
        setDeferredOutputError(null);
    }, [output]);

    useEffect(() => {
        if (!canLoadDeferredOutput || !deferredOutputRequestTarget || !doc.onLoadOutput) {
            return;
        }

        const requestKey = deferredExternalRef
            ? `${definitionId}\u0000${deferredExternalRef.protocol}\u0000${deferredExternalRef.locator}`
            : `${definitionId}\u0000import-symbol\u0000${importNamespace ?? ""}`;
        if (deferredOutputRequestKeyRef.current === requestKey) return;
        deferredOutputRequestKeyRef.current = requestKey;

        let cancelled = false;
        setIsLoadingDeferredOutput(true);
        setDeferredOutputError(null);

        doc.onLoadOutput(deferredOutputRequestTarget)
            .catch((err) => {
                if (cancelled) return;
                setDeferredOutputError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDeferredOutput(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        canLoadDeferredOutput,
        deferredExternalRef,
        deferredOutputRequestTarget,
        definitionId,
        doc.onLoadOutput,
        importNamespace,
    ]);

    const outputHash = useMemo(
        () => (output && shouldPrepareRenderedOutput
            ? buildAnnotationOutputHash({
                output,
                externalRef: deferredExternalRef,
            })
            : undefined),
        [deferredExternalRef, output, shouldPrepareRenderedOutput],
    );

    if (!definition) return null;

    if (!useBinding && !isTemplateDefinition && definition.params && definition.params.length > 0) {
        return (
            <TemplateUsageWarning
                unitId={definitionId}
                kindLabel={t("render.kind.definition")}
                technicalLabel={definition.pluginName}
                params={definition.params}
                mode={mode}
                badge={{ main: "D" }}
                locationLabel="config"
            />
        );
    }

    if (blocksAsTemplate) {
        const templatePlaceholder = getTemplatePlaceholderPresentation(
            t,
            definitionId,
            definition,
            useBinding,
            missingRequiredTemplateInputs,
        );
        return (
            <TemplateRenderPlaceholder
                unitId={definitionId}
                kindLabel={templatePlaceholder.kindLabel}
                technicalLabel={definition.pluginName}
                params={definition.params}
                statusLabel={templatePlaceholder.statusLabel}
                message={templatePlaceholder.message}
                paramLabel={templatePlaceholder.paramLabel}
                mode={mode}
                badgeMain={templatePlaceholder.badgeMain}
                badgeSub={templatePlaceholder.badgeSub}
                configureLabel={templatePlaceholder.configureLabel}
                onConfigure={
                    doc.onDefinitionSelect
                        ? () => doc.onDefinitionSelect?.(definitionId)
                        : undefined
                }
            />
        );
    }

    // No output yet — placeholder with optional Run button
    if (!plugin?.Renderer || !output) {
        const isPluginLoading = !plugin &&
            !!doc.registry.isLoading?.(definition.pluginName);
        const pluginDisplayName = plugin
            ? getPluginDisplayName(plugin.manifest)
            : definition.pluginName;
        const pluginTechnicalLabel = plugin
            ? getPluginTechnicalLabel(plugin.manifest)
            : definition.pluginName;
        const missingOutputText = isPluginLoading
            ? t("render.placeholder.loadingPlugin")
            : !plugin
                ? `Plugin "${definition.pluginName}" not installed`
                : isLoadingDeferredOutput
                    ? t("render.placeholder.loadingOutput")
                    : deferredOutputError
                        ? t("render.placeholder.outputLoadFailed", {
                            error: deferredOutputError,
                        })
                        : deferredOutputLocator
                            ? t("render.placeholder.externalOutput")
                            : importNamespace
                                ? t("render.placeholder.importNotRun")
                                : t("render.placeholder.notRun");
        const statusLabel = isRunning
            ? t("status.running")
            : isImportRunning
                ? t("render.status.importing")
                : isPluginLoading || isLoadingDeferredOutput
                    ? t("status.loading")
                    : hasError || deferredOutputError
                        ? t("render.status.failed")
                        : !plugin
                            ? t("render.status.notInstalled")
                            : deferredOutputLocator
                                ? t("render.status.pendingLoad")
                                : t("render.status.notRun");
        if (isInline) {
            return (
                <button
                    type="button"
                    data-magic-md-input-confirm="true"
                    className={`inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded cursor-pointer border-none ${hasError ? "theme-accent-button" : "bg-muted hover:bg-muted/80"}`}
                    style={hasError ? getThemeAccentStyle("error") : undefined}
                    onClick={handlePrimaryRun}
                    title={errorMessage ?? missingOutputText}
                >
                    {isPrimaryRunning || isPluginLoading || isLoadingDeferredOutput ? (
                        <span
                            className="inline-flex h-2 w-2 rounded-full animate-pulse theme-accent-fill"
                            style={getThemeAccentStyle("running")}
                        />
                    ) : hasError ? (
                        <span
                            className="theme-accent-text"
                            style={getThemeAccentStyle("error")}
                        >
                            ⚠
                        </span>
                    ) : (
                        <span className="text-primary">▶</span>
                    )}
                    {definitionId}
                </button>
            );
        }

        return (
            <div ref={renderContainerRef}>
                <PluginRenderEmptyState
                    definitionId={definitionId}
                    pluginDisplayName={pluginDisplayName}
                    pluginTechnicalLabel={pluginTechnicalLabel}
                    unitBadge={{ main: "D" }}
                    statusLabel={statusLabel}
                    message={missingOutputText}
                    canRun={!!plugin && canRunPrimary}
                    isRunning={isPrimaryRunning}
                    isLoading={isPluginLoading || isLoadingDeferredOutput}
                    hasError={hasError || !!deferredOutputError}
                    errorMessage={errorMessage ?? deferredOutputError}
                    onRun={handlePrimaryRun}
                />
                {!plugin && !isPluginLoading && (
                    <div
                        className="mt-1 text-xs theme-accent-text"
                        style={getThemeAccentStyle("warning")}
                    >
                        Plugin &quot;{definition.pluginName}&quot; not installed
                    </div>
                )}
            </div>
        );
    }

    const Renderer = plugin.Renderer as ComponentType<RendererProps & {
        onConfigChange?: RendererProps["onConfigChange"];
    }>;
    const executionComputedAtLabel =
        execComputedAt != null
            ? new Date(execComputedAt).toLocaleTimeString()
            : null;
    const pluginVersionLabel = `${plugin.manifest.name}@${plugin.manifest.version}`;
    const footerStatusLabel = isImportRunning
        ? t("render.importRunning", { namespace: importNamespace ?? "" })
        : isRunning
            ? (execProgress?.message ?? t("render.status.runningEllipsis"))
            : hasError
                ? (errorMessage ?? t("render.status.failed"))
                : (justFinished || execStatus === "success")
                    ? t("render.status.ran")
                    : null;
    const footerTitle = [
        footerStatusLabel,
        !isImportRunning && execDuration != null ? `${execDuration}ms` : null,
        !isImportRunning ? executionComputedAtLabel : null,
        pluginVersionLabel,
    ].filter((item): item is string => Boolean(item)).join(" · ");

    const renderRunIcon = () => (
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,3 20,12 6,21" /></svg>
    );

    if (!isInline && !expanded) {
        const collapsedStatus: CollapsedUnitRenderStatus = isImportRunning
            ? "running"
            : hasError
                ? "error"
                : isRunning
                    ? "running"
                    : (justFinished || execStatus === "success")
                        ? "success"
                        : null;
        return (
            <Wrapper>
                <CollapsedUnitRenderHeader
                    unitId={definitionId}
                    label={getPluginDisplayName(plugin.manifest)}
                    technicalLabel={getPluginTechnicalLabel(plugin.manifest)}
                    status={collapsedStatus}
                    statusLabels={errorMessage ? { error: errorMessage } : undefined}
                    onExpand={() => setExpanded(true)}
                />
            </Wrapper>
        );
    }

    return (
        <Wrapper className={isInline ? "inline" : undefined}>
            {isInline ? (
                <span className="inline-run-wrapper group">
                    {hasError && (
                        <span
                            className="text-[10px] mr-0.5 theme-accent-text"
                            style={getThemeAccentStyle("error")}
                            title={errorMessage ?? undefined}
                        >
                            ⚠
                        </span>
                    )}
                    {isRunning ? (
                        <span
                            className="inline-flex h-2 w-2 mr-0.5 align-middle rounded-full animate-pulse theme-accent-fill"
                            style={getThemeAccentStyle("running")}
                        />
                    ) : justFinished ? (
                        <span
                            className="inline-flex items-center text-[10px] mr-0.5 align-middle run-success-flash theme-accent-text"
                            style={getThemeAccentStyle("success")}
                        >
                            ✓
                        </span>
                    ) : canRunDefinition ? (
                        <button
                            type="button"
                            data-magic-md-input-confirm="true"
                            className="inline-flex items-center text-[10px] text-muted-foreground/60 hover:text-primary mr-0.5 align-middle cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-150 border-none bg-transparent p-0"
                            onClick={handleRun}
                            title="Re-run"
                        >
                            ▶
                        </button>
                    ) : null}
                    <PluginErrorBoundary
                        pluginName={definition.pluginName}
                        definitionId={definitionId}
                        resetKey={execStatus}
                    >
                        <ContinuationProvider value={continuationCtx}>
                            <AnnotationRenderedTextScope
                                blockId={`render:${definitionId}`}
                                outputHash={outputHash}
                                inline
                            >
                                <MemoizedPluginRendererHost
                                    Renderer={Renderer}
                                    config={definition.config}
                                    output={output}
                                    mode={mode}
                                    attrs={rendererAttrs}
                                    layout={rendererLayout}
                                    definitionId={definitionId}
                                    documentPath={doc.documentPath}
                                    documentBundlePath={doc.documentBundlePath}
	                                    onAction={stableRendererAction}
	                                    callApi={stableRendererApi}
	                                    onConfigChange={stableRendererConfigChange}
                                    callSidecar={doc.callSidecar}
                                    onEngineNotification={doc.onEngineNotification}
                                    selectFile={doc.selectFile}
                                    selectFolder={doc.selectFolder}
                                    openExternal={doc.openExternal}
                                    getAssetUrl={doc.getAssetUrl}
                                    getDefinitions={doc.getDefinitions}
                                    getTabs={doc.getTabs}
                                    editMode={doc.editMode ?? false}
                                />
                            </AnnotationRenderedTextScope>
                        </ContinuationProvider>
                    </PluginErrorBoundary>
                </span>
            ) : (
                <div
                    ref={renderContainerRef}
                    className={cn(
                        "relative magic-render-unit group/plugin-render",
                        isFullscreen
                            ? "fixed z-[9000] m-0 flex flex-col overflow-hidden bg-preview-surface p-4 text-foreground"
                            : undefined,
                    )}
                    style={fullscreenContainerStyle}
                    data-render-fullscreen={isFullscreen ? "true" : undefined}
                    data-render-fullscreen-scope={isFullscreen ? "workspace" : undefined}
                >
                    {/* Error banner */}
                    {hasError && (
                        <div
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] border rounded-t-lg theme-accent-banner"
                            style={getThemeAccentStyle("error")}
                        >
                            <span className="font-medium">⚠ {t("common.error")}:</span>
                            <span className="truncate">{errorMessage}</span>
                            {canRunDefinition && (
                                <div className="ml-auto shrink-0 flex items-center gap-1">
                                    <button
                                        type="button"
                                        data-magic-md-input-confirm="true"
                                        className="px-2 py-0.5 rounded text-[10px] font-medium theme-accent-button"
                                        style={getThemeAccentStyle("error")}
                                        onClick={handleRun}
                                    >
                                        ↻ {t("common.retry")}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Running shimmer */}
                    {(isRunning || isImportRunning) && (
                        <>
                            <div
                                className="absolute inset-x-0 top-0 z-10 h-[2px] overflow-hidden rounded-t-lg theme-accent-progress-track"
                                style={getThemeAccentStyle("running")}
                            >
                                <div
                                    className="h-full w-2/5 rounded-full running-shimmer"
                                    style={{
                                        background: "linear-gradient(to right, transparent, var(--theme-accent), transparent)",
                                        ...getThemeAccentStyle("running"),
                                    }}
                                />
                            </div>
                            <div className="absolute inset-0 bg-background/20 z-[9] rounded-lg pointer-events-none" />
                        </>
                    )}
                    <div
                        ref={renderBodyRef}
                        className={cn(
                            "magic-render-body",
                            isFullscreen ? "magic-render-fullscreen-content" : undefined,
                        )}
                        data-render-layout-presentation={rendererLayout.presentation}
                        data-render-layout-fill-height={rendererLayout.fillHeight ? "true" : "false"}
                        data-render-fullscreen-width-preset={isFullscreen ? fullscreenWidthPreset : undefined}
                        data-md-rendered-output-hash={outputHash}
                        style={renderBodyStyle}
                    >
                        {isFullscreen && (
                            <>
                                <button
                                    type="button"
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label={t("render.fullscreen.resizeLeft")}
                                    title={t("render.fullscreen.resizeTitle")}
                                    className="group/resize absolute inset-y-0 left-0 z-20 w-2 -translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                    onMouseDown={(event) => startFullscreenWidthResize("left", event)}
                                    onDoubleClick={resetFullscreenWidth}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                            isFullscreenWidthDragging
                                                ? "bg-accent"
                                                : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                        )}
                                    />
                                </button>
                                <button
                                    type="button"
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label={t("render.fullscreen.resizeRight")}
                                    title={t("render.fullscreen.resizeTitle")}
                                    className="group/resize absolute inset-y-0 right-0 z-20 w-2 translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                    onMouseDown={(event) => startFullscreenWidthResize("right", event)}
                                    onDoubleClick={resetFullscreenWidth}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                            isFullscreenWidthDragging
                                                ? "bg-accent"
                                                : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                        )}
                                    />
                                </button>
                            </>
                        )}
                        {isFullscreen ? (
                            <div className="magic-render-fullscreen-content-inner">
                                <PluginErrorBoundary
                                    pluginName={definition.pluginName}
                                    definitionId={definitionId}
                                    resetKey={execStatus}
                                >
                                    <ContinuationProvider value={continuationCtx}>
                                        <AnnotationRenderedTextScope
                                            blockId={`render:${definitionId}`}
                                            outputHash={outputHash}
                                        >
                                            <MemoizedPluginRendererHost
                                                Renderer={Renderer}
                                                config={definition.config}
                                                output={output}
                                                mode={mode}
                                                attrs={rendererAttrs}
                                                layout={rendererLayout}
                                                definitionId={definitionId}
                                                documentPath={doc.documentPath}
                                                documentBundlePath={doc.documentBundlePath}
	                                                onAction={stableRendererAction}
	                                                callApi={stableRendererApi}
	                                                onConfigChange={stableRendererConfigChange}
                                                callSidecar={doc.callSidecar}
                                                onEngineNotification={doc.onEngineNotification}
                                                selectFile={doc.selectFile}
                                                selectFolder={doc.selectFolder}
                                                openExternal={doc.openExternal}
                                                getAssetUrl={doc.getAssetUrl}
                                                getDefinitions={doc.getDefinitions}
                                                getTabs={doc.getTabs}
                                                editMode={doc.editMode ?? false}
                                            />
                                        </AnnotationRenderedTextScope>
                                    </ContinuationProvider>
                                </PluginErrorBoundary>
                            </div>
                        ) : !shouldMountRenderer ? (
                            <DeferredPluginRendererPlaceholder
                                definitionId={definitionId}
                                pluginName={definition.pluginName}
                            />
                        ) : (
                            <PluginErrorBoundary
                                pluginName={definition.pluginName}
                                definitionId={definitionId}
                                resetKey={execStatus}
                            >
                                <ContinuationProvider value={continuationCtx}>
                                    <AnnotationRenderedTextScope
                                        blockId={`render:${definitionId}`}
                                        outputHash={outputHash}
                                    >
                                        <MemoizedPluginRendererHost
                                            Renderer={Renderer}
                                            config={definition.config}
                                            output={output}
                                            mode={mode}
                                            attrs={rendererAttrs}
                                            layout={rendererLayout}
                                            definitionId={definitionId}
                                            documentPath={doc.documentPath}
                                            documentBundlePath={doc.documentBundlePath}
	                                            onAction={stableRendererAction}
	                                            callApi={stableRendererApi}
	                                            onConfigChange={stableRendererConfigChange}
                                            callSidecar={doc.callSidecar}
                                            onEngineNotification={doc.onEngineNotification}
                                            selectFile={doc.selectFile}
                                            selectFolder={doc.selectFolder}
                                            openExternal={doc.openExternal}
                                            getAssetUrl={doc.getAssetUrl}
                                            getDefinitions={doc.getDefinitions}
                                            getTabs={doc.getTabs}
                                            editMode={doc.editMode ?? false}
                                        />
                                    </AnnotationRenderedTextScope>
                                </ContinuationProvider>
                            </PluginErrorBoundary>
                        )}
                    </div>
                    {/* Footer toolbar */}
                    {doc.executable && (
                        <RenderUnitFooterDisclosure
                            autoOpen={isRunning || isImportRunning || justFinished}
                            footerClassName={cn(
                                "magic-render-footer flex h-7 min-w-0 items-center gap-2 px-2 text-[10px] text-muted-foreground",
                                isFullscreen
                                    ? "sticky bottom-0 z-20 -mx-4 mt-2 shrink-0 border-t border-border bg-preview-surface/95 backdrop-blur"
                                    : undefined,
                            )}
                            footerTitle={footerTitle || undefined}
                            isFullscreen={isFullscreen}
                            toggleLabel={definitionId}
                            data-plugin-render-footer="true"
                        >
                            <div
                                className="magic-render-footer-leading flex min-w-0 flex-1 items-center gap-2 overflow-hidden"
                                data-plugin-render-footer-leading="true"
                            >
                                {isRunning || isImportRunning ? (
                                    <>
                                        <span
                                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-[1.5px] border-t-transparent animate-spin"
                                            style={{
                                                borderColor: getThemeAccentValue("running"),
                                                borderTopColor: "transparent",
                                            }}
                                        />
                                        <span
                                            className="magic-render-footer-message theme-accent-text min-w-0 max-w-[200px] truncate whitespace-nowrap"
                                            style={getThemeAccentStyle("running")}
                                        >
                                            {isImportRunning
                                                ? t("render.importRunning", {
                                                    namespace: importNamespace ?? "",
                                                })
                                                : execProgress?.message ?? t("render.status.runningEllipsis")}
                                        </span>
                                        {!isImportRunning && execProgress?.percent != null && (
                                            <div
                                                className="magic-render-footer-secondary h-1 w-16 shrink-0 overflow-hidden rounded-full theme-accent-progress-track"
                                                style={getThemeAccentStyle("running")}
                                            >
                                                <div
                                                    className="h-full rounded-full transition-[width] duration-300 theme-accent-fill"
                                                    style={{
                                                        ...getThemeAccentStyle("running"),
                                                        width: `${Math.min(100, Math.max(0, execProgress.percent))}%`,
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {hasError ? (
                                            <span
                                                className="magic-render-footer-status inline-flex min-w-0 max-w-[200px] items-center gap-1 whitespace-nowrap theme-accent-text"
                                                style={getThemeAccentStyle("error")}
                                            >
                                                <svg aria-hidden="true" className="shrink-0" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                <span className="magic-render-footer-message min-w-0 truncate">
                                                    {errorMessage ?? t("render.status.failed")}
                                                </span>
                                            </span>
                                        ) : (justFinished || execStatus === "success") ? (
                                            <span
                                                className="magic-render-footer-status inline-flex shrink-0 items-center gap-1 whitespace-nowrap theme-accent-text"
                                                style={getThemeAccentStyle("success")}
                                            >
                                                <svg aria-hidden="true" className="shrink-0" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                <span>{t("render.status.ran")}</span>
                                            </span>
                                        ) : null}
                                        {execDuration != null && !isImportRunning && (
                                            <span className="magic-render-footer-secondary shrink-0 whitespace-nowrap text-muted-foreground/70">
                                                {execDuration}ms
                                            </span>
                                        )}
                                        {executionComputedAtLabel != null && !isImportRunning && (
                                            <span className="magic-render-footer-secondary shrink-0 whitespace-nowrap text-muted-foreground/50">
                                                {executionComputedAtLabel}
                                            </span>
                                        )}
                                    </>
                                )}
                                <span className="magic-render-footer-plugin min-w-0 max-w-[160px] truncate whitespace-nowrap text-muted-foreground/40">
                                    {pluginVersionLabel}
                                </span>
                            </div>
                            <div
                                className="magic-render-footer-actions ml-auto flex shrink-0 items-center gap-1"
                                data-plugin-render-footer-actions="true"
                            >
                                <span className="contents magic-render-footer-action-secondary magic-render-footer-action-annotations">
                                    <AnnotationBlockFooterControl
                                        blockId={`render:${definitionId}`}
                                        label={definitionId}
                                        outputHash={outputHash}
                                    />
                                </span>
                                <UnitFooterCopyButton
                                    primaryText={copyReferenceText}
                                    actions={copyActions}
                                    className="magic-render-footer-action-secondary magic-render-footer-action-copy"
                                />
                                {createElement(UnitDependencyFooterButton, { targetId: definitionId })}
                                {canRunImport && (
                                    <button
                                        type="button"
                                        className={cn(
                                            "magic-render-footer-action-secondary magic-render-footer-action-import flex h-5 w-5 items-center justify-center rounded transition-colors duration-150",
                                            isImportRunning
                                                ? "cursor-wait text-muted-foreground/50"
                                                : "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground",
                                        )}
                                        onClick={handleRunImport}
                                        disabled={isImportRunning}
                                        title={isImportRunning
                                            ? t("render.importRunning", {
                                                namespace: importNamespace ?? "",
                                            })
                                            : t("render.rerunImport", {
                                                namespace: importNamespace ?? "",
                                            })}
                                        aria-label={isImportRunning
                                            ? t("render.importRunning", {
                                                namespace: importNamespace ?? "",
                                            })
                                            : t("render.rerunImport", {
                                                namespace: importNamespace ?? "",
                                            })}
                                    >
                                        {isImportRunning ? (
                                            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                                        ) : (
                                            <RefreshCw size={11} aria-hidden="true" />
                                        )}
                                    </button>
                                )}
                                {doc.onDebug && (
                                    <button
                                        type="button"
                                        className="magic-render-footer-action-tertiary magic-render-footer-action-debug flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer transition-colors duration-150"
                                        onClick={(e) => { e.stopPropagation(); doc.onDebug?.(definitionId); }}
                                        title="Debug"
                                    >
                                        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
                                    </button>
                                )}
                                {doc.onDefinitionSelect && (
                                    <button
                                        type="button"
                                        className="magic-render-footer-action-primary magic-render-footer-action-config flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                        onClick={(e) => { e.stopPropagation(); doc.onDefinitionSelect?.(definitionId); }}
                                        aria-label={t("definitionPanel.config")}
                                    >
                                        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>
                                    </button>
                                )}
                                {doc.onPin && (
                                    <button
                                        type="button"
                                        className={`magic-render-footer-action-secondary magic-render-footer-action-pin flex items-center justify-center h-5 w-5 rounded transition-colors duration-150 ${isPinned
                                            ? "theme-accent-text"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                                            }`}
                                        style={isPinned ? getThemeAccentStyle("warning") : undefined}
                                        onClick={(e) => { e.stopPropagation(); doc.onPin?.(definitionId); }}
                                        title={isPinned ? t("render.unpin") : t("render.pinToSidebar")}
                                    >
                                        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill={isPinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>
                                    </button>
                                )}
                                {isFullscreen && (
                                    <div className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen-width relative">
                                        <button
                                            type="button"
                                            className="flex items-center justify-center h-5 min-w-5 rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setIsFullscreenWidthMenuOpen((open) => !open);
                                            }}
                                            title={t("render.fullscreen.adjustWidthTitle", {
                                                label: currentFullscreenWidthLabel,
                                            })}
                                            aria-label={t("render.fullscreen.adjustWidth")}
                                            aria-haspopup="menu"
                                            aria-expanded={isFullscreenWidthMenuOpen}
                                        >
                                            <StretchHorizontal size={11} />
                                        </button>
                                        {isFullscreenWidthMenuOpen && (
                                            <div
                                                role="menu"
                                                className="absolute right-0 bottom-6 z-30 w-28 overflow-hidden rounded-md border border-border bg-background py-1 text-[11px] shadow-lg"
                                            >
                                                {PREVIEW_WIDTH_PRESETS.map((preset) => (
                                                    <button
                                                        key={preset.id}
                                                        type="button"
                                                        role="menuitemradio"
                                                        aria-checked={fullscreenWidthPreset === preset.id}
                                                        className={cn(
                                                            "flex w-full items-center justify-between px-2 py-1 text-left transition-colors hover:bg-muted",
                                                            fullscreenWidthPreset === preset.id
                                                                ? "text-foreground"
                                                                : "text-muted-foreground",
                                                        )}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleFullscreenWidthPresetSelect(preset.id);
                                                        }}
                                                    >
                                                        <span>{t(`previewWidth.${preset.id}`)}</span>
                                                        <span className="font-mono text-[10px] text-muted-foreground/60">
                                                            {preset.width === "fill" ? "100%" : preset.width}
                                                        </span>
                                                    </button>
                                                ))}
                                                {fullscreenWidthPreset === "custom" && customFullscreenWidth && (
                                                    <div className="border-t border-border px-2 py-1 text-muted-foreground">
                                                        {t("previewWidth.customWithWidth", {
                                                            width: Math.round(customFullscreenWidth),
                                                        })}
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    role="menuitem"
                                                    className="flex w-full items-center px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        resetFullscreenWidth();
                                                    }}
                                                >
                                                    {t("previewWidth.reset")}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                    onClick={handleToggleFullscreen}
                                    title={isFullscreen ? t("render.fullscreen.exit") : t("render.fullscreen.enter")}
                                    aria-label={isFullscreen ? t("render.fullscreen.exit") : t("render.fullscreen.enter")}
                                >
                                    {isFullscreen ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
                                </button>
                                {canRunDefinition && (
                                    <button
                                        type="button"
                                        className={`magic-render-footer-action-primary magic-render-footer-action-run flex items-center justify-center h-5 w-5 rounded transition-colors duration-150 ${isRunning
                                            ? "text-muted-foreground/50 cursor-wait"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                                        }`}
                                        onClick={(e) => { e.stopPropagation(); handleRun(e); }}
                                        disabled={isRunning}
                                        title="Run"
                                    >
                                        {renderRunIcon()}
                                    </button>
                                )}
                            </div>
                        </RenderUnitFooterDisclosure>
                    )}
                </div>
            )}
        </Wrapper>
    );
});
